const CUSTOMER_SCORE_KEY = 'pixum_customer_score_neukunde';
const CUSTOMER_SCORE_API = 'https://ml-engine-rest-api.pixum-api.com/scoring/neukunde/';

export const CUSTOMER_SCORE_THRESHOLD = 0.5;

interface StoredCustomerScore {
  scoring: string;
  scoring_type_version: string;
}

interface CustomerScoreApiResponse {
  score: string;
  scoring_type_version: string;
  scoring_type: string;
}

/**
 * Rounds a raw score string to one decimal place — mirrors Gatsby's roundCustomerScore().
 */
export function roundCustomerScore(score: string): number {
  return Math.round(parseFloat(score) * 10) / 10;
}

/**
 * Reads the cached "neukunde" score from localStorage.
 * Returns the rounded numeric score, or null when no score is stored yet.
 */
export function getStoredCustomerScore(): number | null {
  try {
    const raw = localStorage.getItem(CUSTOMER_SCORE_KEY);

    if (!raw) return null;

    const parsed = JSON.parse(raw) as StoredCustomerScore;
    const rounded = roundCustomerScore(parsed.scoring);

    return isNaN(rounded) ? null : rounded;
  } catch {
    return null;
  }
}

/**
 * Fetches the "neukunde" customer score from the ML scoring API and caches it in localStorage.
 * Returns the rounded score on success, or null on failure.
 *
 * Fire-and-forget safe: caller may discard the returned Promise.
 */
export async function fetchAndStoreCustomerScore(jentisUserId: string): Promise<number | null> {
  try {
    const response = await fetch(`${CUSTOMER_SCORE_API}${jentisUserId}`);

    if (!response.ok) return null;

    const text = await response.text();

    if (!text) return null;

    const data = JSON.parse(text) as CustomerScoreApiResponse;

    if (!data.score) return null;

    localStorage.setItem(
      CUSTOMER_SCORE_KEY,
      JSON.stringify({ scoring: data.score, scoring_type_version: data.scoring_type_version } satisfies StoredCustomerScore),
    );

    return roundCustomerScore(data.score);
  } catch {
    return null;
  }
}
